<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="exercise-definition-dialog-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form
        ref="form"
        class="exercise-definition-dialog-form"
        @submit.prevent="onSubmit"
      >
        <div class="exercise-definition-dialog-form-body">
          <v-toolbar
            flat
            height="76"
            class="exercise-definition-dialog__toolbar"
          >
            <v-toolbar-title class="exercise-definition-dialog-toolbar-title"
              >{{ editedExercise ? 'Edit' : 'New' }}
              {{ label }}</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container v-if="mode == 'CHOOSE'">
            <v-list class="exercise-definition-dialog--list">
              <v-list-item class="exercise-definition-dialog--list-item">
                <div class="exercise-definition-dialog--desc">
                  <div class="exercise-definition-dialog--icon exercise">
                    <v-icon size="16">bhi-exercise</v-icon>
                  </div>
                  <h5>Single exercise</h5>
                  <v-spacer></v-spacer>
                  <v-btn small icon text @click="mode = 'EXERCISE'">
                    <v-icon>bhi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <p>
                  Choose when you want to add a single exercise that is not in
                  the library
                </p>
              </v-list-item>
              <v-list-item class="exercise-definition-dialog--list-item">
                <div class="exercise-definition-dialog--desc">
                  <div class="exercise-definition-dialog--icon series">
                    <v-icon size="16">bhi-list</v-icon>
                  </div>
                  <h5>Circuit</h5>
                  <v-spacer></v-spacer>
                  <v-btn small icon text @click="mode = 'SERIES'">
                    <v-icon>bhi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <p>
                  Choose when you want to create new Circuit from existing
                  Exercises in your library
                </p>
              </v-list-item>
              <v-list-item class="exercise-definition-dialog--list-item">
                <div class="exercise-definition-dialog--desc">
                  <div class="exercise-definition-dialog--icon activity">
                    <v-icon size="16">bhi-activity</v-icon>
                  </div>
                  <h5>
                    {{ tenantTexts.exerciseDefinitionDialog.ActivityLabel }}
                  </h5>
                  <v-spacer></v-spacer>
                  <v-btn small icon text @click="mode = 'ACTIVITY'">
                    <v-icon>bhi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <p>
                  Choose when you want to add a simple task only with text and
                  image, without metrics
                </p>
              </v-list-item>
            </v-list>
          </v-container>
          <v-container
            class="exercise-definition-dialog-partial"
            v-else-if="mode == 'EXERCISE'"
          >
            <single-exercise-definition-partial
              v-if="dialog"
              :wait-for-submit-response="waitForSubmitResponse"
              :exercise-definitions="exerciseDefinitions"
              :exercise-resource="exerciseResource"
              :exercise-resource-additional="exerciseResourceAdditional"
              :get-resource="getResource"
              :readonly="readonly"
              :edited-exercise="editedExercise"
              :cancelable="editedExercise ? false : true"
              :add-alternatives="addAlternatives"
              :participants-exercises="participantsExercises"
              :parameters-enabled="parametersEnabled"
              :pagination="pagination"
              @cancel="mode = 'CHOOSE'"
              :hide-prev="hidePrev"
              :show-add-to-collection="showAddToCollection"
              :show-additional-media="showAdditionalMedia"
              :readonly-media="readonlyMedia"
              :repetition-options-enabled="repetitionOptionsEnabled"
              v-on="$listeners"
            />
          </v-container>
          <v-container
            class="exercise-definition-dialog-partial"
            v-else-if="mode == 'SERIES'"
          >
            <series-exercise-definition-partial
              v-if="dialog"
              :wait-for-submit-response="waitForSubmitResponse"
              :exercise-definitions="exerciseDefinitions"
              :exercise-resource="exerciseResource"
              :exercise-resource-additional="exerciseResourceAdditional"
              :get-resource="getResource"
              :readonly="readonly"
              :edited-exercise="editedExercise"
              :mode="mode"
              :exercise-definitions-loading="exerciseDefinitionsLoading"
              :cancelable="
                disableCancel ? false : editedExercise ? false : true
              "
              :add-to-series="addToSeries"
              :parameters-enabled="parametersEnabled"
              :participants-exercises="participantsExercises"
              :pagination="pagination"
              @cancel="mode = 'CHOOSE'"
              :hide-prev="hidePrev"
              :repetition-options-enabled="repetitionOptionsEnabled"
              v-on="$listeners"
            />
          </v-container>
          <v-container class="exercise-definition-dialog-partial" v-else>
            <task-exercise-definition-partial
              v-if="dialog"
              :wait-for-submit-response="waitForSubmitResponse"
              :exercise-definitions="exerciseDefinitions"
              :exercise-resource="exerciseResource"
              :exercise-resource-additional="exerciseResourceAdditional"
              :get-resource="getResource"
              :readonly="readonly"
              :edited-exercise="editedExercise"
              :cancelable="editedExercise ? false : true"
              :parameters-enabled="parametersEnabled"
              @cancel="mode = 'CHOOSE'"
              :hide-prev="hidePrev"
              :show-add-to-collection="showAddToCollection"
              :show-additional-media="showAdditionalMedia"
              :readonly-media="readonlyMedia"
              :repetition-options-enabled="repetitionOptionsEnabled"
              v-on="$listeners"
            />
          </v-container>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import SingleExerciseDefinitionPartial from '@/components/dialogs/SingleExerciseDefinitionPartial.vue'
import SeriesExerciseDefinitionPartial from '@/components/dialogs/SeriesExerciseDefinitionPartial.vue'
import TaskExerciseDefinitionPartial from '@/components/dialogs/TaskExerciseDefinitionPartial.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'

const emptyForm = {}

export default {
  name: 'ExerciseDefinitionDialog',
  components: {
    SingleExerciseDefinitionPartial,
    SeriesExerciseDefinitionPartial,
    TaskExerciseDefinitionPartial,
  },
  mixins: [TenantTextsMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    getResource: {
      type: Function,
      default: () => null,
    },
    exerciseResource: {
      type: [Boolean, Object, null],
      default: false,
    },
    exerciseResourceAdditional: {
      type: [Boolean, Object, null],
      default: false,
    },
    exerciseId: {
      type: [Number, null],
      default: null,
    },
    enabledNotes: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    readonlyMedia: {
      type: Boolean,
      default: false,
    },
    showAdditionalMedia: {
      type: Boolean,
      default: true,
    },
    getMimeTypes: {
      type: Function,
      default: () => null,
    },
    exerciseDefinitions: {
      type: Array,
      default: () => [],
    },
    editedExercise: {
      type: [Boolean, Object],
      default: false,
    },
    exerciseDefinitionsLoading: {
      type: Boolean,
      default: false,
    },
    addToSeries: {
      type: Boolean,
      default: true,
    },
    showAddToCollection: {
      type: Boolean,
      default: false,
    },
    addAlternatives: {
      type: Boolean,
      default: false,
    },
    initialMode: {
      type: [Boolean, String],
      default: false,
    },
    participantsExercises: {
      type: Boolean,
      default: false,
    },
    parametersEnabled: {
      type: Boolean,
      default: false,
    },
    disableCancel: {
      type: Boolean,
      default: false,
    },
    hidePrev: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => {},
    },
    repetitionOptionsEnabled: {
      type: Boolean,
      default: false,
    },
    waitForSubmitResponse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      mode: 'CHOOSE',
      modes: ['CHOOSE', 'EXERCISE', 'SERIES', 'ACTIVITY'],
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        if (this.editedExercise) this.mode = this.editedExercise.type
        if (this.initialMode) this.mode = this.initialMode
      } else {
        this.dialog = false
        if (this.waitForSubmitResponse) {
          this.$emit('close-exercise-dialog')
          this.$emit('clear-resource')
        }
      }
    },
  },
  mounted() {
    this.getStaticMedia()
  },
  computed: {
    label: function() {
      return this.editedExercise
        ? this.editedExercise.type == 'EXERCISE'
          ? 'single exercise'
          : this.editedExercise.type == 'SERIES'
          ? 'Circuit'
          : this.tenantTexts.exerciseDefinitionDialog.activityLabel
        : this.mode == 'SERIES'
        ? 'Circuit'
        : this.mode == 'ACTIVITY'
        ? this.tenantTexts.exerciseDefinitionDialog.ActivityLabel
        : 'Exercise'
    },
  },
  methods: {
    ...mapActions('staticMediaStates', ['getStaticMedia']),
    closeDialog() {
      this.$emit('close-exercise-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.exercise-definition-dialog {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-partial {
    flex-grow: 1;
  }
  &__toolbar {
    flex-grow: 0;
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &__tabs {
    flex-grow: 0;
  }
  &__tabs-items {
    background-color: $bh-blue--4;
    flex-grow: 1;
    & > .v-window__container {
      height: 100%;
      flex-grow: 1;
    }
  }
  &--list {
    background-color: $bh-blue--4 !important;
    &-item {
      flex-direction: column;
      background-color: $bh-white;
      border-radius: 8px;
      align-items: stretch;
      margin-bottom: 10px;
      padding: 8px 16px;
      p {
        margin-bottom: 0;
      }
      &::after {
        display: none;
      }
    }
  }
  &--desc {
    display: flex;
    align-items: center;
    h5 {
      margin-bottom: 0;
    }
  }
  &--icon {
    margin-right: 8px;
    color: $bh-white;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    .v-icon {
      color: $bh-white;
    }
    &.exercise {
      background-color: #945eff;
    }
    &.series {
      background-color: #fac032;
    }
    &.activity {
      background-color: #ed8938;
    }
  }
  &__tab {
    height: 100%;
    background-color: $bh-blue--4;
    display: flex;
    flex-direction: column;
    & > .v-tabs-items {
      overflow: auto;
      flex-grow: 1;
      background-color: $bh-blue--4;
    }
    .v-input--switch {
      margin: 0;
    }
    .v-label {
      font-size: 12px;
      color: $bh-gray--2;
    }
  }
  &__dots {
    span {
      width: 16px;
      height: 16px;
      display: inline-block;
      border-radius: 50%;
      background-color: $bh-white;
      margin: 0 3px;
      transition-duration: 300ms;
      &.active {
        background-color: $bh-blue;
      }
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1 * $gutter;
      padding-right: 1 * $gutter;
      h5 {
        margin-top: $gutter/2;
        margin-bottom: $gutter/4;
      }
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-button {
    margin-right: 10px;
    .v-btn {
      cursor: pointer;
    }
    & > label {
      cursor: pointer;
    }
    label {
    }
    .v-btn::before {
      display: none;
    }
    .v-icon {
      &:first-child {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }
  &-file-details {
    display: inline-block;
    vertical-align: middle;
    flex-grow: 1;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-right: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-right: 10px;
    }
    .v-icon {
      margin-left: 5px;
      padding: 4px;
    }
    span {
      cursor: default;
      white-space: nowrap;
      max-width: calc(100% - 80px);
      display: inline-block;
      vertical-align: middle;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }

  .file-uploads {
    vertical-align: middle;
    overflow: auto !important;
    &.disabled {
      pointer-events: none;
      .v-btn {
        background-color: rgba(0, 0, 0, 0.12) !important;
        color: rgba(0, 0, 0, 0.26) !important;
      }
    }
  }
}
</style>
